"use client"

// components/tracker/readouts-page.tsx
//
// Phase 1 (Tripwire Act 2.6): the catalyst calendar — every upcoming
// estimated readout across the member's tracked drugs as a vertical
// timeline (changelog-style date rail). Data is the landscape surface
// the layout already fetched; buildReadoutCalendar does the shaping.
// Honesty rules ride along: month-only dates render month-only, year-only
// dates sit in a "month to be confirmed" bucket, and every headline number
// is derived from the same entries the list renders.
//
// Timeline | Calendar tabs: same data, two views — the changelog rail, or a
// month-grid that only pins day-precise dates.

import Link from "next/link"
import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import * as TabsPrimitive from "@radix-ui/react-tabs"
import { usePathname } from "next/navigation"
import { format, parse, startOfMonth } from "date-fns"

import { CalendarConferenceRail } from "@/components/tracker/calendar-conference-rail"
import { ConferenceCard } from "@/components/tracker/conference-card"
import { CalendarDrugStrip } from "@/components/tracker/calendar-drug-strip"
import { CalendarHero } from "@/components/tracker/calendar-hero"
import { formatCtgovDate, humanizeDeltaDays } from "@/components/tracker/change-copy"
import {
  DrugIdentityProvider,
  useDrugAvatar,
  useDrugIdentityResolver,
} from "@/components/tracker/drug-identity-context"
import { mergeDrugRoster } from "@/lib/pipeline-watch/drug-roster"
import { accentForDrug, avatarUriForDrug } from "@/lib/drug-avatars/avatar-cache"
import { displayName, phaseWord } from "@/components/tracker/track-copy"
import {
  buildReadoutCalendar,
  readoutPrecision,
  readoutTypeLabel,
  type ReadoutCalendar,
  type ReadoutEntry,
} from "@/components/tracker/readouts-view"
import { WatchBar } from "@/components/tracker/recent-activity"
import { ExpiredUpgradeCard } from "@/components/tracker/upgrade-rail"
import { useTrackerShell } from "@/components/tracker/tracker-shell-context"
import { hasWatch } from "@/components/tracker/use-tracker-watchlist"
import { useMarkTrackerSurfaceSeen } from "@/components/tracker/tracker-unread-context"
import { TrialDetailDrawer } from "@/components/tracker/trials-page"
import { useTrialRow } from "@/components/tracker/use-trial-row"
import {
  FullScreenCalendar,
  type CalendarBand,
  type FullScreenCalendarDay,
} from "@/components/ui/fullscreen-calendar"
import { acronymFor } from "@/components/tracker/likely-conferences-card"
import type { WatchConference } from "@/lib/conferences/watch-conferences"
import { readoutCandidatesFor } from "@/lib/conferences/readout-candidates"
import styles from "@/components/tracker/readouts-page.module.css"

function readoutAnchorId(nctId: string, date: string): string {
  return `readout-${nctId}-${date}`
}

function whenPhrase(e: ReadoutEntry): string {
  const precision = readoutPrecision(e.date)
  if (precision === "month") return "Day not recorded"
  if (precision === "year") return "Month and day not recorded"
  if (e.daysUntil === 0) return "Due now"
  if (e.daysUntil <= 90) return `In ${e.daysUntil} ${e.daysUntil === 1 ? "day" : "days"}`
  return `In ${humanizeDeltaDays(e.daysUntil)}`
}

/** Map day-precise readouts into FullScreenCalendar buckets.
 *  Month/year-only registry dates are omitted — inventing a day would lie.
 *
 *  LA2-0's chip is DRUG-FIRST: the bold line is the drug and the mono line
 *  underneath is "phase · estimate|readout". The pre-port mapping had those
 *  the other way round (trial title bold, drug in the meta), which buried the
 *  one word a BD reader scans the grid for. `accent` is the drug's sphere
 *  colour so the chip dot and the halo wear the same hue the rest of the
 *  tracker gives that drug.
 *
 *  THE ACCENT IS THE SPHERE'S `fill` (founder review, 2026-08-26): the
 *  avatar's dominant mid stop, from `avatarAccentColor` on the SAME seed the
 *  avatar is drawn from, so the dot beside a sphere is that sphere's own
 *  colour by construction. Phase A shipped a text shade here, so
 *  Empagliflozin's chip dot rendered purple beside its rust-orange sphere
 *  everywhere else, the "wrong avatar" the founder flagged.
 *
 *  This is a plain helper, not a component, so it cannot run the identity
 *  hooks per event; the caller hands it the provider's resolver
 *  (`useDrugIdentityResolver`) and `accentForDrug` / `avatarUriForDrug`
 *  apply the one seed rule `useDrugColor` uses (FE-DRUG-AVATAR-1, 3b).
 */
function readoutsToCalendarData(
  cal: ReadoutCalendar,
  resolveAssetId: (name: string) => string | null,
): { data: FullScreenCalendarDay[]; initialDate: Date | undefined } {
  const byDay = new Map<string, FullScreenCalendarDay>()
  let id = 1
  for (const month of cal.months) {
    for (const e of month.entries) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(e.date)) continue
      let bucket = byDay.get(e.date)
      if (!bucket) {
        bucket = {
          day: parse(e.date, "yyyy-MM-dd", new Date()),
          events: [],
        }
        byDay.set(e.date, bucket)
      }
      const phase = phaseWord(e.phases)
      const phaseLabel = phase
        ? phase.charAt(0).toUpperCase() + phase.slice(1)
        : null
      const kind = readoutTypeLabel(e.type).toLowerCase()
      const assetId = e.drug ? resolveAssetId(e.drug) : null
      bucket.events.push({
        id: id++,
        name: e.drug ? displayName(e.drug) : (e.briefTitle ?? e.nctId),
        description: e.briefTitle ?? e.nctId,
        time: phaseLabel ? `${phaseLabel} \u00b7 ${kind}` : `Readout \u00b7 ${kind}`,
        datetime: e.date,
        nctId: e.nctId,
        accent: e.drug ? accentForDrug(assetId, e.drug).fill : undefined,
        /* Founder round 2: the chip leads with the drug's REAL sphere, not a
           colour dot \u2014 the same asset-seeded face as every other surface. */
        avatar: e.drug ? avatarUriForDrug(assetId, e.drug) : undefined,
      })
    }
  }
  const data = [...byDay.values()].sort(
    (a, b) => a.day.getTime() - b.day.getTime(),
  )
  return {
    data,
    initialDate: data[0]?.day,
  }
}

function ReadoutsTimelineList({
  cal,
  onOpen,
}: {
  cal: ReadoutCalendar
  onOpen: (nctId: string) => void
}) {
  if (cal.months.length === 0) {
    return (
      <p className={styles.empty}>
        No upcoming readout dates on the registry for your tracked trials.
        The nightly check will catch new ones as sponsors post them.
      </p>
    )
  }
  return (
    <ol className={styles.timeline}>
      {cal.months.map((month) => (
        <Fragment key={month.key}>
          <li className={styles.monthMarker}>
            <span className={styles.monthMeta} aria-hidden />
            <div className={styles.rail} aria-hidden />
            <span className={styles.monthLabel}>{month.label}</span>
          </li>
          {month.entries.map((e) => (
            <ReadoutItem
              key={`${e.nctId}-${e.date}`}
              entry={e}
              onOpen={onOpen}
            />
          ))}
        </Fragment>
      ))}
    </ol>
  )
}

function CloseIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      aria-hidden
    >
      <path d="M6 6l12 12M18 6 6 18" strokeLinecap="round" />
    </svg>
  )
}

/** Day-detail modal — same shell language as the track-preview confirm modal. */
/** One day-modal row's sphere. A component so the identity hook runs once
 *  per row rather than inside the list's map. */
function DayModalAvatar({ drug }: { drug: string }) {
  const { src, identity } = useDrugAvatar(drug)
  return (
    <span
      className={styles.dayModalAvatar}
      data-identity={identity}
      style={{ backgroundImage: `url("${src}")` }}
      aria-hidden
    />
  )
}

function ReadoutDayModal({
  day,
  entries,
  onClose,
  onOpenTrial,
  returnFocus,
}: {
  day: Date
  entries: ReadoutEntry[]
  onClose: () => void
  onOpenTrial: (nctId: string) => void
  returnFocus: HTMLElement | null
}) {
  const dateLabel = format(day, "d MMMM yyyy")

  return (
    <DialogPrimitive.Root open onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className={styles.dayModalBackdrop} />
        <DialogPrimitive.Content
          className={styles.dayModalDialog}
          onCloseAutoFocus={(event) => {
            event.preventDefault()
            returnFocus?.focus()
          }}
        >
        <div className={styles.dayModalHeader}>
          <div className={styles.dayModalIdentity}>
            {/* Founder round 2026-08-26: the boxed monogram plate is gone —
                the new page language is hairlines over boxes, so the date
                stands as plain Archivo display over a hairline rule. */}
            <DialogPrimitive.Title className={styles.dayModalTitle}>
              {dateLabel}
            </DialogPrimitive.Title>
            <DialogPrimitive.Description className={styles.dayModalSub}>
              {entries.length.toLocaleString("en-GB")} readout
              {entries.length === 1 ? "" : "s"}
              {" on your watch"}
            </DialogPrimitive.Description>
          </div>
          <DialogPrimitive.Close asChild>
            <button type="button" className={styles.dayModalClose} aria-label="Close readouts for this day">
              <CloseIcon />
            </button>
          </DialogPrimitive.Close>
        </div>

        <ul className={styles.dayModalList}>
          {entries.map((entry) => {
            const phase = phaseWord(entry.phases)
            const phaseLabel = phase
              ? phase.charAt(0).toUpperCase() + phase.slice(1)
              : null
            return (
              <li key={`${entry.nctId}-${entry.date}`}>
                <button
                  type="button"
                  className={styles.dayModalRow}
                  onClick={() => {
                    onClose()
                    onOpenTrial(entry.nctId)
                  }}
                >
                  {entry.drug ? (
                    /* The REAL sphere avatar, the same asset-seeded face as
                       every other surface (founder round: the letter-orbs
                       are gone). */
                    <DayModalAvatar drug={entry.drug} />
                  ) : null}
                  <span className={styles.dayModalRowBody}>
                    <span className={styles.dayModalRowTitle}>
                      {entry.briefTitle ?? entry.nctId}
                    </span>
                    <span className={styles.dayModalRowMeta}>
                      {entry.drug ? `${displayName(entry.drug)} · ` : ""}
                      {phaseLabel ? `${phaseLabel} · ` : ""}
                      {readoutTypeLabel(entry.type)}
                      {" · "}
                      {entry.nctId}
                    </span>
                  </span>
                  <span className={styles.dayModalRowCta}>View trial →</span>
                </button>
              </li>
            )
          })}
        </ul>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

function MobileDayAgenda({ day, entries, onOpenTrial }: {
  day: Date
  entries: ReadoutEntry[]
  onOpenTrial: (nctId: string) => void
}) {
  return (
    <section className={styles.mobileAgenda} aria-labelledby="mobile-agenda-title">
      <div className={styles.mobileAgendaHead}>
        <h2 id="mobile-agenda-title">{format(day, "d MMMM yyyy")}</h2>
        <span>{entries.length} {entries.length === 1 ? "readout" : "readouts"}</span>
      </div>
      <ul>
        {entries.length === 0 ? (
          <li className={styles.mobileAgendaEmpty}>No readouts scheduled for this day.</li>
        ) : entries.map((entry) => (
          <li key={`${entry.nctId}-${entry.date}`}>
            <button type="button" onClick={() => onOpenTrial(entry.nctId)}>
              {entry.drug ? <DayModalAvatar drug={entry.drug} /> : null}
              <span>
                <strong>{entry.briefTitle ?? entry.nctId}</strong>
                <small>{entry.drug ? `${displayName(entry.drug)} · ` : ""}{readoutTypeLabel(entry.type)} · {entry.nctId}</small>
              </span>
              <b aria-hidden>↗</b>
            </button>
          </li>
        ))}
      </ul>
    </section>
  )
}

function ReadoutsCalendarView({
  cal,
  watchSummary,
  landscapeId,
  onOpenTrial,
}: {
  cal: ReadoutCalendar
  watchSummary: string
  landscapeId: string | null
  onOpenTrial: (nctId: string) => void
}) {
  const resolveAssetId = useDrugIdentityResolver()
  const { data, initialDate } = useMemo(
    () => readoutsToCalendarData(cal, resolveAssetId),
    [cal, resolveAssetId],
  )
  const [modalDay, setModalDay] = useState<Date | null>(null)
  const [mobileAgendaDay, setMobileAgendaDay] = useState<Date | null>(null)
  const modalTriggerRef = useRef<HTMLElement | null>(null)
  const [isMobile, setIsMobile] = useState(false)
  useEffect(() => {
    const media = window.matchMedia("(max-width: 719px)")
    const update = () => setIsMobile(media.matches)
    update()
    media.addEventListener("change", update)
    return () => media.removeEventListener("change", update)
  }, [])
  /* CONTROLLED MONTH (FE-READOUTS-1 Phase B). Lifted so the conference
     rail's jump-to-month can drive the grid; controlled from mount, because
     handing FullScreenCalendar control mid-life would drop it back to
     whatever month it last owned. */
  const [month, setMonth] = useState<Date>(() =>
    startOfMonth(initialDate ?? new Date()),
  )
  /* CONFERENCE BANDS (founder round 2026-08-26; SCOPE narrowed round 3).
     The rail reports the venue payload it fetched, but the grid bands show
     CANDIDATE MEETINGS ONLY — an edition earns a band iff at least one of
     the watch's expected readouts falls inside its [start, end] window (the
     LQJ-0 "YOUR CONFERENCE WATCH" semantics). The RAIL stays the full
     likely-venues list; the two surfaces now answer different questions and
     each says which.

     Precision rule (stated per the plan): a DAY-precise readout counts via
     inclusive ISO string compare (start <= d <= end, the grid's own
     day-cell boundary semantics); a MONTH-only readout ("YYYY-MM") counts
     if the edition window overlaps any day of that month; a YEAR-only
     readout never qualifies a band — a whole year "overlapping" a 4-day
     congress is a claim the data does not make. An edition with a NULL
     end_date is treated as a SINGLE-DAY window (end = start): the start is
     the only day the row asserts, and inventing a longer window would
     band days the data never claimed (round 4, N6). */
  const [venues, setVenues] = useState<WatchConference[]>([])
  const onVenues = useCallback((v: WatchConference[]) => setVenues(v), [])
  /* ONE candidate predicate, shared (founder reversal, Phase E ship round):
     the memo yields the bands AND the set of venue slugs that earned at
     least one of them — the RAIL now renders only those venues, so bands
     and rail can never disagree about which meetings are candidates. */
  const { bands, candidateSlugs } = useMemo<{
    bands: CalendarBand[]
    candidateSlugs: Set<string>
  }>(() => {
    /* Phase E: entries keep their NCT so each venue can count ONLY the
       readouts of trials that EARNED it (relevantNcts, the composer's
       provenance) — a respiratory congress no longer claims a diabetes
       readout just because both sit on one watch. */
    const dayEntries: { date: string; nctId: string }[] = []
    const monthEntries: { key: string; nctId: string }[] = []
    for (const m of cal.months) {
      for (const e of m.entries) {
        if (/^\d{4}-\d{2}-\d{2}$/.test(e.date))
          dayEntries.push({ date: e.date, nctId: e.nctId })
        else if (/^\d{4}-\d{2}$/.test(e.date))
          monthEntries.push({ key: e.date, nctId: e.nctId })
      }
    }
    const out: CalendarBand[] = []
    const slugs = new Set<string>()
    for (const v of venues) {
      const relevant = new Set(v.relevantNcts ?? [])
      for (const ed of v.editions) {
        const end = ed.endDate ?? ed.startDate
        const startMonth = ed.startDate.slice(0, 7)
        const endMonth = end.slice(0, 7)
        const hits =
          dayEntries.filter(
            (e) =>
              relevant.has(e.nctId) &&
              e.date >= ed.startDate &&
              e.date <= end,
          ).length +
          monthEntries.filter(
            (e) =>
              relevant.has(e.nctId) &&
              startMonth <= e.key &&
              e.key <= endMonth,
          ).length
        /* A band renders only with >= 1 RELEVANT in-window readout — the
           Phase C candidate-band rule, tightened by provenance. */
        if (hits === 0) continue
        slugs.add(v.slug)
        out.push({
          key: `${v.slug}-${ed.startDate}`,
          label: acronymFor(v),
          color: v.brand?.hex ?? null,
          /* The M53-0 gradient partner: the society's secondary, else the
             band CSS derives a darkened partner from the brand itself. */
          color2: v.brand?.secondaryHex ?? null,
          start: parse(ed.startDate, "yyyy-MM-dd", new Date()),
          end: parse(end, "yyyy-MM-dd", new Date()),
          /* The honesty affordance NAMES the band's own window (Phase E):
             this count is readouts DURING THE MEETING DAYS, distinct from
             the card's labelled asymmetric window (four months before the
             meeting starts to two weeks after it ends). */
          /* House rule: no em dashes in human copy. */
          hint: `${v.name} · ${hits.toLocaleString("en-GB")} readout${
            hits === 1 ? "" : "s"
          } during the meeting`,
        })
      }
    }
    return { bands: out, candidateSlugs: slugs }
  }, [venues, cal])

  /* THE CONFERENCE CARD (FE-READOUTS-1 Phase D). A band click opens N4V-0's
     card for that edition; the rail keeps its jump-to-month click. The band's
     key (`${slug}-${startDate}`, minted in the bands memo above) is the
     lookup handle back into the venue payload. */
  const [cardBandKey, setCardBandKey] = useState<string | null>(null)
  const cardTarget = useMemo(() => {
    if (!cardBandKey) return null
    for (const v of venues) {
      for (const ed of v.editions) {
        if (`${v.slug}-${ed.startDate}` === cardBandKey) {
          return { venue: v, edition: ed }
        }
      }
    }
    return null
  }, [cardBandKey, venues])

  /* THE CARD'S CANDIDATE WINDOW — deliberately WIDER than the band rule, and
     NAMED on the card ("four months before to two weeks after the meeting" —
     the lookback widened from two on 2026-08-27, see the calibration note in
     lib/conferences/readout-window.ts). The whole rule —
     relevance (Phase E provenance) AND the asymmetric window, plus the
     soonest-first sort and the truncated-backward-arm honesty note — lives in
     ONE place, lib/conferences/readout-candidates.ts (lifted in BE-CONF-1
     Phase 3b.1 so the Conferences page applies the identical rule). */
  const cardCandidates = useMemo<ReadoutEntry[]>(() => {
    if (!cardTarget) return []
    return readoutCandidatesFor(
      cardTarget.venue,
      cardTarget.edition,
      cal.months.flatMap((m) => m.entries),
    )
  }, [cardTarget, cal])

  const modalEntries = useMemo(() => {
    if (!modalDay) return []
    const key = format(modalDay, "yyyy-MM-dd")
    return cal.months
      .flatMap((m) => m.entries)
      .filter((e) => e.date === key)
  }, [cal, modalDay])
  const mobileAgendaEntries = useMemo(() => {
    if (!mobileAgendaDay) return []
    const key = format(mobileAgendaDay, "yyyy-MM-dd")
    return cal.months
      .flatMap((m) => m.entries)
      .filter((e) => e.date === key)
  }, [cal, mobileAgendaDay])

  if (cal.months.length === 0) {
    return (
      <p className={styles.empty}>
        No upcoming readout dates on the registry for your tracked trials.
        The nightly check will catch new ones as sponsors post them.
      </p>
    )
  }

  if (data.length === 0) {
    return (
      <p className={styles.empty}>
        Upcoming readouts are month- or year-stamped on the registry — they
        need a day before they can sit on the calendar. Switch to Timeline to
        see them.
      </p>
    )
  }

  return (
    <>
      <FullScreenCalendar
        data={data}
        initialDate={initialDate}
        month={month}
        onMonthChange={setMonth}
        watchSummary={watchSummary}
        /* The next readout on the grid is the first day-precise entry, and the
           list is already sorted ascending over upcoming entries only. */
        highlightDay={data[0]?.day}
        /* THE CONFERENCE RAIL (FE-READOUTS-1 Phase B fills the column Phase A
           reserved): the watch's likely venues as an acronym decay ladder.
           Clicking a dated edition jumps the grid via the controlled month. */
        bands={bands}
        rail={
          <CalendarConferenceRail
            landscapeId={landscapeId}
            onJumpToMonth={(m) => setMonth(startOfMonth(m))}
            onVenues={onVenues}
            /* Founder reversal (Phase E ship round): the rail renders only
               CANDIDATE venues — the same predicate the bands passed, from
               the same memo, so the two can never disagree. */
            candidateSlugs={candidateSlugs}
          />
        }
        afterGrid={
          isMobile && mobileAgendaDay ? (
            <MobileDayAgenda
              day={mobileAgendaDay}
              entries={mobileAgendaEntries}
              onOpenTrial={onOpenTrial}
            />
          ) : null
        }
        onDaySelect={(day, _events, trigger) => {
          modalTriggerRef.current = trigger
          if (isMobile) setMobileAgendaDay(day)
          else setModalDay(day)
        }}
        onSelectedDayChange={isMobile ? setMobileAgendaDay : undefined}
        /* Phase D: a band click's job is the Conference card. */
        onBandSelect={(band) => setCardBandKey(band.key)}
      />
      {cardTarget ? (
        <ConferenceCard
          venue={cardTarget.venue}
          edition={cardTarget.edition}
          candidates={cardCandidates}
          onClose={() => setCardBandKey(null)}
          onOpenTrial={onOpenTrial}
        />
      ) : null}
      {!isMobile && modalDay && modalEntries.length > 0 ? (
        <ReadoutDayModal
          day={modalDay}
          entries={modalEntries}
          onClose={() => setModalDay(null)}
          onOpenTrial={onOpenTrial}
          returnFocus={modalTriggerRef.current}
        />
      ) : null}
    </>
  )
}

export function ReadoutItem({
  entry,
  onOpen,
}: {
  entry: ReadoutEntry
  onOpen: (nctId: string) => void
}) {
  const face = useDrugAvatar(entry.drug)
  const phase = phaseWord(entry.phases)
  const trialName = entry.briefTitle
  const phaseLabel = phase
    ? phase.charAt(0).toUpperCase() + phase.slice(1)
    : null

  return (
    <li
      id={readoutAnchorId(entry.nctId, entry.date)}
      className={styles.item}
    >
      <div className={styles.meta}>
        <span className={styles.datePill}>{formatCtgovDate(entry.date)}</span>
        <span className={styles.when}>{whenPhrase(entry)}</span>
      </div>
      <div className={styles.rail} aria-hidden>
        <span className={styles.dot} />
      </div>
      <button
        type="button"
        className={styles.content}
        onClick={() => onOpen(entry.nctId)}
      >
        {entry.drug && face.src ? (
          <div className={styles.miniCard}>
            {/* Founder round 2: the timeline's letter orb becomes the drug's
                real sphere — same asset-seeded face, same 32px slot. */}
            <span
              className={styles.miniMonogram}
              data-identity={face.identity ?? undefined}
              style={{
                backgroundImage: `url("${face.src}")`,
                backgroundSize: "cover",
                backgroundPosition: "50%",
              }}
              aria-hidden
            />
            <div className={styles.miniId}>
              <h2 className={styles.miniName}>{displayName(entry.drug)}</h2>
              {trialName ? (
                <p className={styles.miniTrial}>{trialName}</p>
              ) : null}
              <p className={styles.miniSub}>
                {phaseLabel ? `${phaseLabel} · ` : ""}
                {readoutTypeLabel(entry.type)} readout
              </p>
            </div>
          </div>
        ) : (
          <>
            <h2 className={styles.headline}>Readout</h2>
            {trialName ? <p className={styles.summary}>{trialName}</p> : null}
          </>
        )}
        <div className={styles.chips}>
          <span className={styles.nctId}>{entry.nctId}</span>
          {phaseLabel ? (
            <span className={styles.phaseChip}>{phaseLabel}</span>
          ) : null}
          <span className={styles.typeChip}>
            {readoutTypeLabel(entry.type)}
          </span>
        </div>
        <span className={styles.review}>View trial →</span>
      </button>
    </li>
  )
}

export function ReadoutsPage() {
  const { watchlist, surface } = useTrackerShell()
  const pathname = usePathname()
  const [openNct, setOpenNct] = useState<string | null>(null)
  const [activeView, setActiveView] = useState<"timeline" | "calendar">("calendar")
  const tabListRef = useRef<HTMLDivElement>(null)
  const [tabListNode, setTabListNode] = useState<HTMLDivElement | null>(null)
  const timelineLabelRef = useRef<HTMLSpanElement>(null)
  const calendarLabelRef = useRef<HTMLSpanElement>(null)
  const [tabIndicator, setTabIndicator] = useState({
    left: 0,
    width: 0,
    ready: false,
  })
  const [tabIndicatorCanAnimate, setTabIndicatorCanAnimate] = useState(false)
  const captureTabList = useCallback((node: HTMLDivElement | null) => {
    tabListRef.current = node
    setTabListNode(node)
  }, [])
  const syncTabIndicator = useCallback(() => {
    const list = tabListRef.current
    const label = activeView === "timeline"
      ? timelineLabelRef.current
      : calendarLabelRef.current
    if (!list || !label) return
    const listRect = list.getBoundingClientRect()
    const labelRect = label.getBoundingClientRect()
    setTabIndicator({
      left: labelRect.left - listRect.left,
      width: labelRect.width,
      ready: true,
    })
  }, [activeView])

  useLayoutEffect(syncTabIndicator, [syncTabIndicator, tabListNode])
  useEffect(() => {
    const list = tabListNode
    if (!list) return
    const observer = new ResizeObserver(syncTabIndicator)
    observer.observe(list)
    return () => observer.disconnect()
  }, [syncTabIndicator, tabListNode])
  useEffect(() => {
    if (!tabIndicator.ready) return
    const frame = window.requestAnimationFrame(() => {
      setTabIndicatorCanAnimate(true)
    })
    return () => window.cancelAnimationFrame(frame)
  }, [tabIndicator.ready])
  const landscapeId =
    watchlist.status === "ready" ? watchlist.landscapeId : null

  // Readouts span the whole watchlist, but the surface only carries the
  // trials its change feed references, so the drawer's row is fetched on
  // demand rather than read out of `surface.trials`. Hook stays above the
  // early returns below; it no-ops until a readout is actually opened.
  const openTrial = useTrialRow(
    landscapeId,
    openNct,
  )

  useMarkTrackerSurfaceSeen(
    "readouts",
    !!landscapeId && !!surface && !surface.gated,
  )

  const card =
    "rounded-[18px] border border-ink/10 bg-[#f8f9fa] px-8 py-10 text-center " +
    "shadow-[4px_4px_10px_rgba(13,31,45,0.12),-3px_-3px_8px_rgba(255,255,255,0.95)]"

  if (watchlist.status === "signed_out") {
    return (
      <div className={card}>
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-ink/45">
          Trial Tracker
        </p>
        <h1 className="mt-2 text-xl font-semibold tracking-tight text-ink">
          Readouts is a member page
        </h1>
        <p className="mx-auto mt-2 max-w-md text-sm text-ink/60">
          Sign in to your tracker to see it.
        </p>
        <Link
          href={`/login?next=${encodeURIComponent(pathname ?? "/tools/trial-tracker/calendar")}`}
          className="mt-6 inline-flex h-10 items-center rounded-full bg-ink px-5 text-sm font-medium text-white transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/40"
        >
          Sign in
        </Link>
      </div>
    )
  }

  if (watchlist.status === "ready" && !hasWatch(watchlist, surface)) {
    return (
      <div className={card}>
        <h1 className="text-xl font-semibold tracking-tight text-ink">
          Track a drug to build your readout calendar
        </h1>
        <p className="mx-auto mt-2 max-w-md text-sm text-ink/60">
          Every upcoming readout across your watch lands here on a
          single timeline.
        </p>
        <Link
          href="/tools/trial-tracker"
          className="mt-6 inline-flex h-10 items-center rounded-full bg-ink px-5 text-sm font-medium text-white transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/40"
        >
          Track a drug
        </Link>
      </div>
    )
  }

  if (surface?.gated) {
    return (
      <ExpiredUpgradeCard
        line="Your catalyst calendar keeps filling as sponsors post and move their dates."
        counts={surface.gatedCounts}
      />
    )
  }

  if (!surface) {
    return (
      <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-ink/50">
        Loading your readout calendar…
      </p>
    )
  }

  const cal = buildReadoutCalendar(surface)
  /* THE ROSTER, NOT THE LIST (FE-PAGE-AUDIT-1 invariant; this page still
     missed it before FE-READOUTS-1 Phase A). A mandate watch lists no drugs,
     so `landscape.drugs` was empty and every readout chip, monogram and the
     "across N drugs" line rendered blank on a watch built entirely from
     attribution. `drugCounts` is keyed by the MERGED roster (listed plus
     attributed) -- the same keying the digest rail and the changes filters
     use, so a drug wears one colour slot across all three. Merged rather than
     swapped, so a listed drug with nothing attributed keeps its place and its
     palette slot. */
  const drugs = mergeDrugRoster(
    surface.landscape?.drugs ?? [],
    Object.keys(surface.drugCounts ?? {}),
  )
  const openRow = openTrial.status === "ready" ? openTrial.row : null

  /* THE WATCH-WIDE HALF of LA2-0's toolbar sub-line. It must say "on your
     watch" out loud, because `cal.total` counts the WHOLE watch including the
     month- and year-precision readouts that can never appear on a day grid --
     and the grid sits under a single month's title. The component prepends the
     count of what is actually painted; this half is the context around it, and
     the undated remainder is stated rather than quietly dropped.

     Same invariant as readouts-view.ts: every count is derived from the same
     entries the surface renders. `cal.total` is the timeline's own total, and
     `undated` is that total minus the entries the calendar could place, so the
     three numbers add up by construction. */
  const datedCount = cal.months.reduce(
    (n, m) =>
      n + m.entries.filter((e) => /^\d{4}-\d{2}-\d{2}$/.test(e.date)).length,
    0,
  )
  const undated = cal.total - datedCount
  const calendarWatchSummary = [
    `${cal.total.toLocaleString("en-GB")} upcoming on your watch`,
    undated > 0
      ? `${undated.toLocaleString("en-GB")} without a day`
      : null,
  ]
    .filter(Boolean)
    .join(" \u00b7 ")

  return (
    <DrugIdentityProvider assetIdByDrug={surface?.assetIdByDrug}>
    <div className={styles.page}>
      {/* LA2-0's carved dusk hero replaces the old masthead + stats card +
          "How this page works" tips strip (both deleted on the approved
          board). The stats inside are the same live numbers the old header
          derived. */}
      <CalendarHero cal={cal} drugs={drugs} trialCount={surface.trialCount} />

      {/* The drug watch strip sits ABOVE the view toggle so both the
          Timeline and Calendar views carry it, as on the board. */}
      <CalendarDrugStrip drugs={drugs} counts={surface.drugCounts} />

      <TabsPrimitive.Root
        value={activeView}
        onValueChange={(value) => setActiveView(value as "timeline" | "calendar")}
        activationMode="manual"
      >
        <TabsPrimitive.List
          ref={captureTabList}
          className={styles.viewTabs}
          aria-label="Calendar view"
        >
          <TabsPrimitive.Trigger value="timeline" className={styles.viewTab}>
            <span ref={timelineLabelRef}>Timeline</span>
          </TabsPrimitive.Trigger>
          <TabsPrimitive.Trigger value="calendar" className={styles.viewTab}>
            <span ref={calendarLabelRef}>Calendar</span>
          </TabsPrimitive.Trigger>
          <span
            aria-hidden="true"
            className={styles.viewTabIndicator}
            data-ready={tabIndicator.ready ? "true" : "false"}
            data-animate={tabIndicatorCanAnimate ? "true" : "false"}
            style={{
              transform: `translateX(${tabIndicator.left}px)`,
              width: `${tabIndicator.width}px`,
            }}
          />
        </TabsPrimitive.List>
        <TabsPrimitive.Content value="calendar" className={styles.viewPanel}>
          <ReadoutsCalendarView
            cal={cal}
            watchSummary={calendarWatchSummary}
            landscapeId={landscapeId}
            onOpenTrial={setOpenNct}
          />
        </TabsPrimitive.Content>
        <TabsPrimitive.Content value="timeline" className={styles.viewPanel}>
          <ReadoutsTimelineList cal={cal} onOpen={setOpenNct} />
        </TabsPrimitive.Content>
      </TabsPrimitive.Root>

      <div className={styles.footerBar}>
        <WatchBar surface={surface} />
      </div>
      <p className={styles.source}>
        Dates and date types are sponsor-reported from ClinicalTrials.gov.
        Actual, estimated, anticipated and unrecorded values are shown as
        recorded. We recheck nightly.
      </p>

      {openRow && landscapeId ? (
        <TrialDetailDrawer
          key={openRow.nctId}
          row={openRow}
          trial={null}
          landscapeId={landscapeId}
          onClose={() => setOpenNct(null)}
          signal={openTrial.status === "ready" ? (openTrial.signal ?? null) : null}
        />
      ) : null}
    </div>
    </DrugIdentityProvider>
  )
}
